import {
  createStyles,
  darken,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
} from '@material-ui/core';
import { teal } from '@material-ui/core/colors';
import * as React from 'react';
import { useEffect } from 'react';
import AnalysisChartLoading from '../../AnalysisChartLoading';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { formatNumber } from '../../../../logic/otherUtilities/formatting/formatNumber';
import { ChartProps } from '../../AnalysisList';
import { internationalText } from '../../internationalText';
import DataTimedOut, { didQueryTimeout } from '../DataTimedOut';
import { NotEnoughData } from '../NotEnoughData';

export interface BestTrafficPagesProps extends ChartProps {}

export const useClasses = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      marginTop: theme.spacing(4),
    },
    tableCell: {
      padding: theme.spacing(1),
      paddingRight: theme.spacing(2),
    },
    link: {
      fontWeight: 500,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  })
);

const BestTrafficPages: React.FC<BestTrafficPagesProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: { showLimitItems: true },
      })),
    []
  );

  // Fetch keyword data
  const bestTrafficUrlsQuery = projectsApiService.queries.analysis.useGetBestTrafficPages({
    projectId: project.id,
    limitItems: analysisFilterState?.limitItems,
    projectDomainId: project.domains?.find((x) => x.type === 'PROJECT').id,
  });

  const loading = bestTrafficUrlsQuery?.query?.loading;
  const result = bestTrafficUrlsQuery?.result;

  //Styling
  const classes = useClasses();

  // Render
  if (didQueryTimeout(bestTrafficUrlsQuery?.query)) {
    return <DataTimedOut />;
  }

  if (loading) {
    return <AnalysisChartLoading />;
  }

  if (!result?.bestTrafficPages?.length) {
    return <NotEnoughData />;
  }

  return (
    <div data-screenshot-id="chart-loaded">
      <Table size="small" className={classes.table} padding="none">
        <colgroup>
          <col width="40px" />
          <col width="60px" />
          <col width="60px" />
          <col />
          <col width="60px" />
          <col />
          <col width="60px" />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell align={'center'}>#</TableCell>
            <TableCell align={'right'} className={classes.tableCell}>
              {internationalText.chart.best_pages_by_traffic.traffic[language]}
            </TableCell>
            <TableCell align={'right'} className={classes.tableCell}>
              {internationalText.chart.best_pages_by_traffic.volume[language]}
            </TableCell>

            <TableCell className={classes.tableCell}>
              {internationalText.chart.best_pages_by_traffic.page_url[language]}
            </TableCell>
            <TableCell align={'right'} className={classes.tableCell}>
              {internationalText.chart.best_pages_by_traffic.keywords[language]}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {internationalText.chart.best_pages_by_traffic.best_keyword[language]}
            </TableCell>
            <TableCell align={'right'} className={classes.tableCell}>
              {internationalText.chart.best_pages_by_traffic.rank[language]}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {result?.bestTrafficPages?.map((page, index) => (
            <TableRow key={page.url}>
              <TableCell align={'center'}>{index + 1}</TableCell>
              <TableCell align={'right'} className={classes.tableCell}>
                {formatNumber(page.totalTraffic, 0)}
              </TableCell>
              <TableCell align={'right'} className={classes.tableCell}>
                {formatNumber(page.totalVolume, 0)}
              </TableCell>

              <TableCell className={classes.tableCell}>
                <a
                  href={page.url}
                  target="_blank"
                  className={classes.link}
                  style={{ color: palette.primaryScale[1] }}
                >
                  {page.url}
                </a>
              </TableCell>
              <TableCell align={'right'} className={classes.tableCell}>
                {formatNumber(page.keywordCount, 0)}
              </TableCell>
              <TableCell className={classes.tableCell}>{page.bestKeyword}</TableCell>
              <TableCell align={'right'} className={classes.tableCell}>
                {formatNumber(page.bestKeywordRank, 0)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default BestTrafficPages;
